import { startTransition } from 'react';
import { graphql, useLazyLoadQuery, useMutation, useRefetchableFragment } from 'react-relay';
import { removeNotification, showNotification } from '../../components/widgets/notificationToaster';
import { T } from '../helpers';
import { SubscriptionDataFragmentRefetchQuery } from './__generated__/SubscriptionDataFragmentRefetchQuery.graphql';
import { useSubscriptionDataMutation } from './__generated__/useSubscriptionDataMutation.graphql';
import { useSubscriptionDataQuery } from './__generated__/useSubscriptionDataQuery.graphql';
import { useSubscriptionData_subscriptionDataFragment$key } from './__generated__/useSubscriptionData_subscriptionDataFragment.graphql';

const subscriptionDataFragment = graphql`
    fragment useSubscriptionData_subscriptionDataFragment on Query @refetchable(queryName: "SubscriptionDataFragmentRefetchQuery") {
        subscription {
            planName
            planVersion
            profileDescription
            profileName
            isPantoneEnabled
            isSinglePrintFileEnabled
            isCmykEnabled
            isPdfEpsEnabled
            isSinglePrintFileEnabled
            isAdvancedPricingEnabled
            isProfanityChecksEnabled
            isOnCustomPlan
            areSalesChannelsEnabled
            inTrial
            remainingTrialDays
            plan {
                name
                description
                version
                currencyCode
                maxSizeStorageMB
                salesChannelPrice
                isCustom
                additionalFeatures {
                    editPDFPreview
                    editSummary
                    integrations
                    bulkVariationForm
                    pricePerImage
                    engraving
                    editPrintfiles
                    printReadyFilesPlus
                    textplus
                    prices3dConfigurator
                    customEnvironmentTexture
                    dynamicProperties
                    materialLibrary
                    multiareaConfiguratorDesigner
                    advancedPrintFilesBuilder
                    aiPoweredTools
                    getAQuote
                    customInterface
                    profanityFilter
                    largeFile
                    configuratorLinks
                    vectorization
                    moq
                    extraOptions
                    standalone
                }
            }
            addons {
                addonID
                name
                startDate
            }
        }
        ecommerceSettings {
            ecommerceName
        }

        seller {
            marketplace
        }

        salesChannels(status: ACTIVE, ignoreNoneECommerce: true) {
            id
            ecommerceSettings {
                ecommerceName
                isMarketplace
                remoteUrl
                currencyCode
            }
        }

        shopifyAuthEcommerceSettings {
            environment
            installed
            isTestStore
            shop
        }
    }
`;

// Hook
export const useSubscriptionData = () => {
    // Queries
    const data = useLazyLoadQuery<useSubscriptionDataQuery>(
        graphql`
            query useSubscriptionDataQuery {
                ...useSubscriptionData_subscriptionDataFragment
            }
        `,
        {}
    );

    const [subscriptionData, refetchSubscriptionData] = useRefetchableFragment<
        SubscriptionDataFragmentRefetchQuery,
        useSubscriptionData_subscriptionDataFragment$key
    >(subscriptionDataFragment, data);

    const updateSubscription = (onComplete?: (error: Error | null) => void) => {
        startTransition(() => {
            refetchSubscriptionData({}, { fetchPolicy: 'network-only', onComplete });
        });
    };

    // Mutations
    const [activateAddonWithKey, isActivatingAddonInProgress] = useMutation<useSubscriptionDataMutation>(graphql`
        mutation useSubscriptionDataMutation($addonKey: String!, $currencyCode: String!) {
            activateAddonWithKey(addonKey: $addonKey, currencyCode: $currencyCode)
        }
    `);

    const activateAddonForSeller = (addonKey: string, currencyCode: string, callbackOnActivated: () => void, showToastNotifications = true) => {
        const activateInProgressNotification = showToastNotifications
            ? showNotification({
                  type: 'default',
                  message: T._('Activating addon...', 'Admin')
              })
            : '';

        activateAddonWithKey({
            onCompleted: (response) => {
                if (showToastNotifications) removeNotification(activateInProgressNotification);
                if (response.activateAddonWithKey !== 'SUCCESS') {
                    showNotification({
                        type: 'danger',
                        message:
                            response.activateAddonWithKey === 'NO_PREMIUM'
                                ? T._('Unable to activate the addon cause you have to upgrade your plan', 'Admin')
                                : T._('An error occurred while activate addon', 'Admin'),
                        dismiss: {
                            duration: 5000
                        }
                    });
                } else {
                    if (showToastNotifications)
                        showNotification({
                            type: 'success',
                            message: T._('Addon activated successfully', 'Admin'),
                            dismiss: {
                                duration: 5000
                            }
                        });

                    if (callbackOnActivated) callbackOnActivated();
                }
            },
            variables: {
                addonKey,
                currencyCode
            }
        });
    };

    return {
        subscription: subscriptionData.subscription,
        ecommerceName: subscriptionData.ecommerceSettings?.ecommerceName,
        isActivatingAddonInProgress,
        shopifyEcommerceSettings: subscriptionData.shopifyAuthEcommerceSettings,
        sellerMarketplace: subscriptionData.seller?.marketplace,
        salesChannels: subscriptionData.salesChannels,
        updateSubscription,
        activateAddonForSeller
    };
};
